import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useQuery, useLazyQuery, gql, useMutation } from '@apollo/client';
import {
    Icon, Container, Header, Segment, Grid,
    Button, Divider, Message, Label, Transition, Placeholder, Dropdown, Modal, Input, Popup
} from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { MediaTile } from '../../../../components/MediaTile';
import { PATHS } from '../../../../paths';
import {
    FILE_INPUT_ACCEPT, MAX_VIDEO_DURATION_SECONDS, putContentTypeForFile,
    uploadContentTypeForFile, validateMediaFiles,
} from '../../../../utils/media';
import styles from './AllGalleryView.module.css';

const GET_GALLERIES = gql`
  query Galleries {
    galleries {
      id
      name
      isDefault
      imageCount
      myAccessLevel
      managers { userId name }
    }
    canCreateGallery
  }
`;

const CREATE_GALLERY = gql`
  mutation CreateGallery($name: String!, $description: String) {
    createGallery(name: $name, description: $description) { id name }
  }
`;

const RENAME_GALLERY = gql`
  mutation RenameGallery($galleryId: Int!, $name: String!) {
    renameGallery(galleryId: $galleryId, name: $name) { id name }
  }
`;

const ARCHIVE_GALLERY = gql`
  mutation ArchiveGallery($galleryId: Int!) {
    archiveGallery(galleryId: $galleryId) { id }
  }
`;

const GET_GALLERY_PEOPLE = gql`
  query GalleryPeople($galleryId: Int!) {
    galleryPeople(galleryId: $galleryId) {
      membershipId
      name
      userName
      level
      sourceType
      isEditable
    }
  }
`;

const SET_GALLERY_ACCESS = gql`
  mutation SetGalleryAccess($galleryId: Int!, $membershipId: Int!, $level: String!) {
    setGalleryAccess(galleryId: $galleryId, membershipId: $membershipId, level: $level)
  }
`;

const REVOKE_GALLERY_ACCESS = gql`
  mutation RevokeGalleryAccess($galleryId: Int!, $membershipId: Int!) {
    revokeGalleryAccess(galleryId: $galleryId, membershipId: $membershipId)
  }
`;

/**
 * What a gallery manager can hand out. CONTRIBUTE is first and is the default — "view and add
 * photos" is what sharing a gallery normally means, and offering VIEW first would make the
 * common case the extra click.
 */
const ACCESS_CHOICES = [
    { key: 'CONTRIBUTE', value: 'CONTRIBUTE', text: 'View and add photos' },
    { key: 'VIEW', value: 'VIEW', text: 'View only' },
    { key: 'MANAGE', value: 'MANAGE', text: 'Manage — can also share it' },
];

const GET_IMAGES = gql`
  query ImagesConnection($cursor: String, $limit: Int, $galleryId: Int) {
    imagesConnection(cursor: $cursor, limit: $limit, galleryId: $galleryId) {
      items { id, url, contentType, durationSeconds }
      nextCursor
      hasNextPage
    }
  }
`;

const GET_SINGLE_IMAGE = gql`
  query Image($imageId: Int!) {
    image(id: $imageId) {
      id
      url
      contentType
      durationSeconds
    }
  }
`;

const CREATE_IMAGE_UPLOAD = gql`
  mutation CreateImageUpload($fileName: String!, $contentType: String, $durationSeconds: Float, $galleryId: Int) {
    createImageUpload(fileName: $fileName, contentType: $contentType, durationSeconds: $durationSeconds, galleryId: $galleryId) {
      image { id }
      uploadUrl
    }
  }
`;

/**
 * Remembers the gallery you were last looking at, so returning to the page does not dump you
 * back in "General" every time. Per-browser and best-effort: the id is validated against the
 * galleries the server actually returns before it is used, which is also what makes it safe
 * when someone switches organisation — a remembered id from another org simply will not match
 * and the default is chosen instead.
 */
const LAST_GALLERY_KEY = 'joyya.gallery.lastUsed';

const readLastGallery = (): number | null => {
    try {
        const stored = window.localStorage.getItem(LAST_GALLERY_KEY);
        const parsed = stored != null ? Number(stored) : NaN;
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    } catch {
        // Private browsing and "block site data" both throw rather than returning null.
        return null;
    }
};

const writeLastGallery = (galleryId: number) => {
    try {
        window.localStorage.setItem(LAST_GALLERY_KEY, String(galleryId));
    } catch {
        // Not being able to remember is not worth breaking the page over.
    }
};

/** A picked file plus the duration we probed while validating it, which the backend re-checks. */
interface StagedFile {
    file: File;
    durationSeconds?: number;
    /** Object URL for the preview. Held so it can be revoked on unstage/unmount. */
    previewUrl: string;
}

// --- Small helper for the top selection bar ---
const SelectedThumbnail = ({ id, onRemove }: { id: number, onRemove: (id: number) => void }) => {
    const { data, loading } = useQuery(GET_SINGLE_IMAGE, { variables: { imageId: id } });

    if (loading) return <Placeholder style={{ height: 60, width: 60, borderRadius: '8px', margin: '0 5px' }}><Placeholder.Image /></Placeholder>;
    if (!data?.image) return null;

    return (
        <div style={{ position: 'relative', display: 'inline-block', margin: '0 8px' }}>
            <MediaTile
                url={data.image.url}
                contentType={data.image.contentType}
                durationSeconds={data.image.durationSeconds}
                style={{ width: '60px', height: '60px', objectFit: 'cover', border: '2px solid #2185d0' }}
                rounded
            />
            <Button
                icon="close" size="mini" color="red" circular
                onClick={() => onRemove(id)}
                style={{ position: 'absolute', top: '-5px', right: '-5px', padding: '3px', fontSize: '8px', zIndex: 5 }}
            />
        </div>
    );
};

const AllGalleryView = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize from URL
    const [selectedImageIds, setSelectedImageIds] = useState<number[]>(() => {
        const params = new URLSearchParams(location.search);
        const ids = params.get('images');
        return ids ? ids.split(',').map(Number).filter(Boolean) : [];
    });

    // The gallery being viewed. URL wins over the remembered one so a shared link always
    // opens what the sender was looking at; null means "let the server pick the default".
    const [selectedGalleryId, setSelectedGalleryId] = useState<number | null>(() => {
        const params = new URLSearchParams(location.search);
        const fromUrl = Number(params.get('gallery'));
        if (Number.isFinite(fromUrl) && fromUrl > 0) {
            return fromUrl;
        }
        return readLastGallery();
    });

    const [showUpload, setShowUpload] = useState(false);
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadErrors, setUploadErrors] = useState<string[]>([]);

    // Preview object URLs are revoked on unstage and after upload; this catches the case
    // where the user navigates away with files still staged.
    const stagedFilesRef = useRef(stagedFiles);
    stagedFilesRef.current = stagedFiles;
    useEffect(() => () => {
        stagedFilesRef.current.forEach(staged => URL.revokeObjectURL(staged.previewUrl));
    }, []);

    // Sync state to URL. Both parameters are written here rather than in two effects, which
    // would race: each reads location.search, so whichever ran second would overwrite the
    // other's parameter with the value it captured before the first one wrote.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (selectedImageIds.length > 0) {
            params.set('images', selectedImageIds.join(','));
        } else {
            params.delete('images');
        }
        if (selectedGalleryId != null) {
            params.set('gallery', String(selectedGalleryId));
        } else {
            params.delete('gallery');
        }
        navigate({ search: params.toString() }, { replace: true });
    }, [selectedImageIds, selectedGalleryId, navigate, location.search]);

    const { data: galleriesData, loading: galleriesLoading } = useQuery(GET_GALLERIES);
    const galleries = galleriesData?.galleries ?? [];

    // Settle on a gallery once the list arrives: keep the current one if it is really ours,
    // otherwise fall back to the first, which the server orders default-then-alphabetical.
    // This is also what quietly corrects a stale localStorage id or a link to a gallery the
    // viewer cannot reach, rather than showing them an empty page with no explanation.
    useEffect(() => {
        if (galleriesLoading || galleries.length === 0) {
            return;
        }
        const stillValid = galleries.some((g: any) => g.id === selectedGalleryId);
        if (!stillValid) {
            setSelectedGalleryId(galleries[0].id);
        }
    }, [galleriesLoading, galleries, selectedGalleryId]);

    useEffect(() => {
        if (selectedGalleryId != null) {
            writeLastGallery(selectedGalleryId);
        }
    }, [selectedGalleryId]);

    const { loading, error, data, fetchMore, refetch } = useQuery(GET_IMAGES, {
        variables: { limit: 12, cursor: null, galleryId: selectedGalleryId },
        // Without a gallery in hand there is nothing useful to ask for yet, and firing now
        // would fetch the server's default only to refetch the real choice a moment later.
        skip: selectedGalleryId == null && galleriesLoading,
    });

    const currentGallery = galleries.find((g: any) => g.id === selectedGalleryId);
    // CONTRIBUTE is the rung that allows uploading — a VIEW-only gallery shows no upload
    // control. The server enforces this too; this only stops us offering an action that
    // would be refused.
    const canUploadHere = currentGallery == null
        ? false
        : ['CONTRIBUTE', 'EDIT', 'MANAGE'].includes(currentGallery.myAccessLevel);
    const canManageHere = currentGallery?.myAccessLevel === 'MANAGE';
    // Whether creating is allowed is the server's answer, not a guess from gallery access —
    // creating needs MANAGE on the *organisation*, which is a different grant entirely.
    const canCreateGallery = galleriesData?.canCreateGallery === true;
    const managers = currentGallery?.managers ?? [];

    const [createImageUpload] = useMutation(CREATE_IMAGE_UPLOAD);

    // Gallery management. `refetchQueries` rather than cache surgery: the list is small, and a
    // rename or archive changes imageCount and myAccessLevel on rows other than the one acted
    // on (archiving moves images into the default gallery), which a manual cache update would
    // get wrong.
    const [createGallery] = useMutation(CREATE_GALLERY, { refetchQueries: [GET_GALLERIES] });
    const [renameGallery] = useMutation(RENAME_GALLERY, { refetchQueries: [GET_GALLERIES] });
    const [archiveGallery] = useMutation(ARCHIVE_GALLERY, { refetchQueries: [GET_GALLERIES] });

    // Sharing. Lazy because the list needs a gallery id and is only wanted when the modal
    // opens — 44 people in the largest organisation, so no pagination needed, but no reason to
    // fetch it on every page load either.
    const [loadPeople, { data: peopleData, loading: peopleLoading, refetch: refetchPeople }] =
        useLazyQuery(GET_GALLERY_PEOPLE);
    const [setGalleryAccess] = useMutation(SET_GALLERY_ACCESS);
    const [revokeGalleryAccess] = useMutation(REVOKE_GALLERY_ACCESS);
    const [peopleFilter, setPeopleFilter] = useState('');
    const [pendingMembershipId, setPendingMembershipId] = useState<number | null>(null);

    const [galleryDialog, setGalleryDialog] = useState<null | 'create' | 'rename' | 'archive' | 'people'>(null);
    const [galleryName, setGalleryName] = useState('');
    const [galleryBusy, setGalleryBusy] = useState(false);
    const [galleryError, setGalleryError] = useState<string | null>(null);

    const openCreate = () => {
        setGalleryName('');
        setGalleryError(null);
        setGalleryDialog('create');
    };

    const openRename = () => {
        setGalleryName(currentGallery?.name ?? '');
        setGalleryError(null);
        setGalleryDialog('rename');
    };

    const submitGalleryDialog = async () => {
        const name = galleryName.trim();
        if (name === '') {
            setGalleryError('A gallery needs a name');
            return;
        }

        setGalleryBusy(true);
        setGalleryError(null);
        try {
            if (galleryDialog === 'create') {
                const { data: created } = await createGallery({ variables: { name } });
                // Drop straight into the new gallery — creating one and then having to find it
                // in the dropdown is a small thing that feels broken.
                if (created?.createGallery?.id) {
                    setSelectedGalleryId(created.createGallery.id);
                }
            } else if (selectedGalleryId != null) {
                await renameGallery({ variables: { galleryId: selectedGalleryId, name } });
            }
            setGalleryDialog(null);
        } catch (err) {
            setGalleryError(err instanceof Error ? err.message : 'That did not work');
        } finally {
            setGalleryBusy(false);
        }
    };

    const openPeople = () => {
        if (selectedGalleryId == null) return;
        setPeopleFilter('');
        setGalleryError(null);
        setGalleryDialog('people');
        loadPeople({ variables: { galleryId: selectedGalleryId } });
    };

    /** null level means "remove their access". */
    const changeAccess = async (membershipId: number, level: string | null) => {
        if (selectedGalleryId == null) return;
        setPendingMembershipId(membershipId);
        setGalleryError(null);
        try {
            if (level == null) {
                await revokeGalleryAccess({ variables: { galleryId: selectedGalleryId, membershipId } });
            } else {
                await setGalleryAccess({ variables: { galleryId: selectedGalleryId, membershipId, level } });
            }
            await refetchPeople();
        } catch (err) {
            // The server refuses a few things the list cannot know in advance — removing the
            // last manager, most importantly — so its message is the useful one.
            setGalleryError(err instanceof Error ? err.message : 'That did not work');
        } finally {
            setPendingMembershipId(null);
        }
    };

    const confirmArchive = async () => {
        if (currentGallery == null) return;
        setGalleryBusy(true);
        setGalleryError(null);
        try {
            await archiveGallery({ variables: { galleryId: currentGallery.id } });
            // Its images moved to the default gallery, so go where they went.
            setSelectedGalleryId(galleries.find((g: any) => g.isDefault)?.id ?? null);
            setGalleryDialog(null);
        } catch (err) {
            setGalleryError(err instanceof Error ? err.message : 'That did not work');
        } finally {
            setGalleryBusy(false);
        }
    };

    const toggleImageSelection = (id: number) => {
        setSelectedImageIds(prev =>
            prev.includes(id) ? prev.filter(imageId => imageId !== id) : [...prev, id]
        );
    };

    const handleConfirmSelection = () => {
        navigate(`/posts/compose?images=${selectedImageIds.join(',')}`);
    };

    /**
     * Single funnel for both the file input and the drop zone. Videos need a duration probe
     * (and a size/format check) before they can be staged, so this is async — files that
     * pass are staged, the rest are reported.
     */
    const stageFiles = async (incoming: File[]) => {
        if (incoming.length === 0) return;

        const { accepted, errors } = await validateMediaFiles(incoming);
        setUploadErrors(errors);
        if (accepted.length === 0) return;

        setStagedFiles(prev => [
            ...prev,
            ...accepted.map(({ file, durationSeconds }) => ({
                file,
                durationSeconds,
                previewUrl: URL.createObjectURL(file),
            })),
        ]);
    };

    const unstageFile = (index: number) => {
        setStagedFiles(prev => {
            const target = prev[index];
            if (target) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    const clearStagedFiles = () => {
        setStagedFiles(prev => {
            prev.forEach(staged => URL.revokeObjectURL(staged.previewUrl));
            return [];
        });
    };

    const performUpload = async () => {
        setIsUploading(true);
        setUploadErrors([]);
        const newImageIds = []
        try {
            for (const { file, durationSeconds } of stagedFiles) {
                const { data: uploadResponse } = await createImageUpload({
                    variables: {
                        fileName: file.name,
                        contentType: uploadContentTypeForFile(file),
                        durationSeconds,
                        // Uploads land in the gallery you are looking at. Null is a valid
                        // value the server reads as "the default gallery", which is what
                        // keeps older mobile builds working.
                        galleryId: selectedGalleryId,
                    },
                });
                if (uploadResponse?.createImageUpload) {
                    const { uploadUrl, image } = uploadResponse.createImageUpload;
                    const { id } = image;
                    const response = await fetch(uploadUrl, {
                        method: 'PUT',
                        body: file,
                        // This header is what sets the stored object's content type — the
                        // presign uses SignedHeaders=host, so the backend's ContentType is
                        // never applied. Videos stored as octet-stream won't play.
                        headers: { 'Content-Type': putContentTypeForFile(file) },
                    });
                    // Without this an S3 rejection looks like a successful upload, and the
                    // Image row the backend already created is left pointing at nothing.
                    if (!response.ok) {
                        throw new Error(`Upload of ${file.name} failed (${response.status}).`);
                    }
                    newImageIds.push(id);
                }
            }
            clearStagedFiles();
            setShowUpload(false);

            // reroute to a chain of tagging
            // TAG_IMAGE: "/gallery/:imageId/tags",
            // gallery/1/tags?nextImageIds=2,3,4 so when i am done with this it will go to the next image
            let path = '';
            if (newImageIds.length > 0) {
                path = `/gallery/${newImageIds[0]}/tags`
            }

            if (newImageIds.length > 1) {
                newImageIds.shift()
                path = `${path}?nextImageIds=${newImageIds.join(',')}`
            }

            if (path.length === 0) {
                refetch()
            } else {
                navigate(path)
            }
        } catch (err) {
            console.error('Upload failed:', err);
            setUploadErrors([err instanceof Error ? err.message : 'Upload failed. Please try again.']);
        } finally {
            setIsUploading(false);
        }
    };

    // Phase 5.9 — the gate is "can you reach any gallery", not "are you a content reviewer".
    // Derived from the list this page already fetches rather than asking a second time: the
    // gate and the switcher are then the same answer by construction.
    //
    // Waiting on `galleriesLoading` matters — an empty list and an unanswered query look
    // identical, so redirecting on the result alone bounces a legitimate user mid-load.
    if (!galleriesLoading && galleries.length === 0) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    return (
        <ResponsiveContainer>
            <Segment basic style={{ padding: '2em 0em' }}>
                <Container>
                    {/* Selection Highlighting Area */}
                    <Transition visible={selectedImageIds.length > 0} animation='fade down' duration={300}>
                        <Segment color="blue" raised style={{ marginBottom: '2em', background: '#f0f7ff', position: 'sticky', top: '64px', zIndex: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', overflowX: 'auto', padding: '10px 0', flex: 1 }}>
                                    {selectedImageIds.map(id => (
                                        <SelectedThumbnail key={id} id={id} onRemove={toggleImageSelection} />
                                    ))}
                                </div>
                                <div style={{ paddingLeft: '20px' }}>
                                    <Button color="blue" size="large" onClick={handleConfirmSelection} icon labelPosition="right">
                                        Confirm {selectedImageIds.length} Items
                                        <Icon name='arrow right' />
                                    </Button>
                                    <Button icon="trash" basic color="red" circular onClick={() => setSelectedImageIds([])} style={{ marginLeft: '10px' }} />
                                </div>
                            </div>
                        </Segment>
                    </Transition>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1em' }}>
                        <Header as='h2' style={{ margin: 0 }}>
                            <Icon name='arrow left' circular link onClick={() => navigate(-1)} />
                            <Header.Content>
                                Gallery
                                <Header.Subheader>
                                    Select images in the order you want them to appear
                                    {/* Who to ask for access. Shown to anyone who can view the
                                        gallery — the people who need it are exactly those who
                                        cannot manage it, so gating this on MANAGE would show it
                                        only to people with nothing to ask for. */}
                                    {managers.length > 0 && (
                                        <Popup
                                            position="bottom left"
                                            trigger={
                                                <span style={{ marginLeft: '0.6em', cursor: 'help', borderBottom: '1px dotted #999' }}>
                                                    <Icon name="user circle" />
                                                    {managers.length === 1 ? '1 manager' : `${managers.length} managers`}
                                                </span>
                                            }
                                        >
                                            <Popup.Header>
                                                Who manages {currentGallery?.name}
                                            </Popup.Header>
                                            <Popup.Content>
                                                <p style={{ marginBottom: '0.5em' }}>
                                                    {canManageHere
                                                        ? 'You can change who has access.'
                                                        : 'Ask one of these people if you need access to add or share photos.'}
                                                </p>
                                                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                                    {managers.map((m: any) => (
                                                        <li key={m.userId}>{m.name}</li>
                                                    ))}
                                                </ul>
                                            </Popup.Content>
                                        </Popup>
                                    )}
                                </Header.Subheader>
                            </Header.Content>
                        </Header>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                            {/* The switcher. Only shown when there is a genuine choice — with a
                                single gallery a dropdown of one is just clutter. Selection
                                survives switching: selectedImageIds is independent of which
                                gallery is on screen, so you can gather images from several and
                                PostCompose receives them all. */}
                            {galleries.length > 1 && (
                                <Dropdown
                                    selection
                                    value={selectedGalleryId ?? undefined}
                                    onChange={(_, { value }) => setSelectedGalleryId(Number(value))}
                                    options={galleries.map((g: any) => ({
                                        key: g.id,
                                        value: g.id,
                                        text: g.name,
                                        description: String(g.imageCount),
                                    }))}
                                />
                            )}

                            {canManageHere && (
                                <Dropdown
                                    button icon="cog" className="icon" floating
                                    title="Manage galleries"
                                    direction="left"
                                >
                                    <Dropdown.Menu>
                                        {canCreateGallery && (
                                            <Dropdown.Item icon="plus" text="New gallery" onClick={openCreate} />
                                        )}
                                        <Dropdown.Item icon="users" text="Who can see this gallery" onClick={openPeople} />
                                        <Dropdown.Item icon="edit" text="Rename this gallery" onClick={openRename} />
                                        {/* The default gallery is the fallback for every client
                                            that names no gallery — including mobile builds in
                                            the field — so it cannot be archived. The server
                                            refuses too; this just avoids offering it. */}
                                        {!currentGallery?.isDefault && (
                                            <Dropdown.Item
                                                icon="archive"
                                                text="Archive this gallery"
                                                onClick={() => { setGalleryError(null); setGalleryDialog('archive'); }}
                                            />
                                        )}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}

                            {canUploadHere && (
                                <Button
                                    color={showUpload ? "black" : "grey"}
                                    icon labelPosition="left"
                                    onClick={() => setShowUpload(!showUpload)}
                                >
                                    <Icon name={showUpload ? "chevron up" : "upload"} />
                                    {showUpload ? "Close Upload" : "Upload New"}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Create / rename / archive. One modal for all three so there is a single
                        place errors from the server surface — permissions, duplicate names and
                        "this is the only person who can manage it" all arrive the same way. */}
                    {/* Sharing. Separate from the create/rename/archive modal because it is a
                        list rather than a single field, and it stays open while you change
                        several people. */}
                    <Modal open={galleryDialog === 'people'} size="small" onClose={() => setGalleryDialog(null)}>
                        <Modal.Header>Who can see {currentGallery?.name}</Modal.Header>
                        <Modal.Content scrolling>
                            <p style={{ color: '#666' }}>
                                Anyone in your organisation with a login can be given access.
                                People without a username and password are not listed — access
                                they could never sign in to use would be access in name only.
                            </p>

                            <Input
                                fluid
                                icon="search"
                                placeholder="Search by name"
                                value={peopleFilter}
                                onChange={(_, { value }) => setPeopleFilter(value)}
                                style={{ marginBottom: '1em' }}
                            />

                            {galleryError && <Message error content={galleryError} />}

                            {peopleLoading && <Placeholder><Placeholder.Line /><Placeholder.Line /></Placeholder>}

                            {!peopleLoading && (peopleData?.galleryPeople ?? [])
                                .filter((p: any) => {
                                    const q = peopleFilter.trim().toLowerCase();
                                    if (q === '') return true;
                                    return String(p.name ?? '').toLowerCase().includes(q)
                                        || String(p.userName ?? '').toLowerCase().includes(q);
                                })
                                .map((p: any) => (
                                    <div
                                        key={p.membershipId}
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            gap: '1em', padding: '0.6em 0', borderBottom: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: p.level ? 600 : 400 }}>{p.name}</div>
                                            <div style={{ color: '#999', fontSize: '0.85em' }}>{p.userName}</div>
                                        </div>

                                        {p.isEditable ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                                <Dropdown
                                                    selection
                                                    compact
                                                    loading={pendingMembershipId === p.membershipId}
                                                    disabled={pendingMembershipId != null}
                                                    placeholder="No access"
                                                    value={p.level ?? ''}
                                                    options={ACCESS_CHOICES}
                                                    onChange={(_, { value }) => changeAccess(p.membershipId, String(value))}
                                                />
                                                {p.level && (
                                                    <Button
                                                        icon="close"
                                                        basic
                                                        title="Remove access"
                                                        disabled={pendingMembershipId != null}
                                                        onClick={() => changeAccess(p.membershipId, null)}
                                                    />
                                                )}
                                            </div>
                                        ) : (
                                            // Access from a role or an org-wide grant. Showing it
                                            // greyed rather than hiding it answers "why can they
                                            // see this?" without implying it can be removed here.
                                            <Label basic title={`From their role — not editable here (${p.sourceType})`}>
                                                <Icon name="lock" />
                                                {p.level === 'MANAGE' ? 'Manages' : p.level}
                                            </Label>
                                        )}
                                    </div>
                                ))}

                            {!peopleLoading && (peopleData?.galleryPeople ?? []).length === 0 && (
                                <Message
                                    info
                                    content="Nobody in this organisation has a login yet, so there is no one to share with."
                                />
                            )}
                        </Modal.Content>
                        <Modal.Actions>
                            <Button onClick={() => setGalleryDialog(null)}>Done</Button>
                        </Modal.Actions>
                    </Modal>

                    <Modal open={galleryDialog != null && galleryDialog !== 'people'} size="tiny" onClose={() => setGalleryDialog(null)}>
                        <Modal.Header>
                            {galleryDialog === 'create' && 'New gallery'}
                            {galleryDialog === 'rename' && 'Rename gallery'}
                            {galleryDialog === 'archive' && 'Archive gallery'}
                        </Modal.Header>
                        <Modal.Content>
                            {galleryDialog === 'archive' ? (
                                <p>
                                    Archive <strong>{currentGallery?.name}</strong>? Its{' '}
                                    {currentGallery?.imageCount ?? 0} image
                                    {currentGallery?.imageCount === 1 ? '' : 's'} will move to the
                                    default gallery — nothing is deleted.
                                </p>
                            ) : (
                                <Input
                                    fluid
                                    autoFocus
                                    placeholder="Gallery name"
                                    value={galleryName}
                                    onChange={(_, { value }) => setGalleryName(value)}
                                    onKeyDown={(e: any) => { if (e.key === 'Enter') submitGalleryDialog(); }}
                                />
                            )}
                            {galleryError && <Message error content={galleryError} style={{ marginTop: '1em' }} />}
                        </Modal.Content>
                        <Modal.Actions>
                            <Button onClick={() => setGalleryDialog(null)} disabled={galleryBusy}>Cancel</Button>
                            {galleryDialog === 'archive' ? (
                                <Button color="red" loading={galleryBusy} onClick={confirmArchive}>Archive</Button>
                            ) : (
                                <Button primary loading={galleryBusy} onClick={submitGalleryDialog}>
                                    {galleryDialog === 'create' ? 'Create' : 'Save'}
                                </Button>
                            )}
                        </Modal.Actions>
                    </Modal>

                    {/* Selecting across galleries is deliberate, but it is confusing without a
                        word of explanation — the thumbnails at the top stop matching the grid. */}
                    {selectedImageIds.length > 0 && galleries.length > 1 && (
                        <Message
                            size="tiny"
                            info
                            style={{ marginTop: '1em' }}
                            content={`Your ${selectedImageIds.length} selected ${selectedImageIds.length === 1 ? 'item stays' : 'items stay'} selected while you switch galleries.`}
                        />
                    )}

                    <Transition visible={showUpload} animation='slide down' duration={300}>
                        <Segment secondary padded style={{ marginTop: '2em', borderRadius: '12px' }}>
                            <div
                                className={styles.dropZone}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    if (e.dataTransfer.files) stageFiles(Array.from(e.dataTransfer.files));
                                }}
                            >
                                <Header icon color="grey">
                                    <Icon name='cloud upload' />
                                    Drag and drop photos or videos here
                                    <Header.Subheader style={{ marginTop: '0.5em', fontWeight: 'normal' }}>
                                        Videos must be {MAX_VIDEO_DURATION_SECONDS} seconds or shorter (MP4 or MOV)
                                    </Header.Subheader>
                                    <Divider horizontal>OR</Divider>
                                    <Button as="label" htmlFor="file-input" basic color="blue">
                                        Select Documents
                                        <input
                                            id="file-input"
                                            type="file"
                                            hidden
                                            multiple
                                            accept={FILE_INPUT_ACCEPT}
                                            onChange={(e) => {
                                                const picked = Array.from(e.target.files ?? []);
                                                // Reset so re-picking a file that was unstaged still fires onChange.
                                                e.target.value = '';
                                                stageFiles(picked);
                                            }}
                                        />
                                    </Button>
                                </Header>
                            </div>

                            {uploadErrors.length > 0 && (
                                <Message
                                    warning
                                    onDismiss={() => setUploadErrors([])}
                                    header={uploadErrors.length > 1 ? "Some files couldn't be added" : "That file couldn't be added"}
                                    list={uploadErrors}
                                />
                            )}

                            {stagedFiles.length > 0 && (
                                <div style={{ marginTop: '20px' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                                        {stagedFiles.map((staged, index) => (
                                            <div key={staged.previewUrl} style={{ position: 'relative' }}>
                                                <MediaTile
                                                    url={staged.previewUrl}
                                                    contentType={staged.file.type}
                                                    durationSeconds={staged.durationSeconds}
                                                    alt="preview"
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                                                />
                                                <Button icon='delete' size='mini' color='red' circular style={{ position: 'absolute', top: '-8px', right: '-8px' }} onClick={() => unstageFile(index)} />
                                            </div>
                                        ))}
                                    </div>
                                    <Button primary fluid size="large" loading={isUploading} onClick={performUpload}>
                                        Upload {stagedFiles.length} {stagedFiles.length === 1 ? 'Item' : 'Items'}
                                    </Button>
                                </div>
                            )}
                        </Segment>
                    </Transition>

                    <Divider section />

                    {error && <Message error content={error.message} />}

                    <Grid doubling stackable columns={4}>
                        {data?.imagesConnection.items.map((img: any) => {
                            const selectedIndex = selectedImageIds.indexOf(img.id);
                            const isSelected = selectedIndex !== -1;
                            return (
                                <Grid.Column key={img.id}>
                                    <div
                                        className={`${styles.imageWrapper} ${isSelected ? styles.selected : ''}`}
                                        onClick={() => toggleImageSelection(img.id)}
                                        style={{ cursor: 'pointer', position: 'relative' }}
                                    >
                                        <MediaTile
                                            url={img.url}
                                            contentType={img.contentType}
                                            durationSeconds={img.durationSeconds}
                                            fluid
                                            rounded
                                            style={{ border: isSelected ? '4px solid #2185d0' : '1px solid #ddd', transition: 'all 0.2s' }}
                                        />

                                        {/* Selection Number Overlay */}
                                        {isSelected && (
                                            <Label color="blue" floating circular style={{ top: '10px', left: '10px', zIndex: 10 }}>
                                                {selectedIndex + 1}
                                            </Label>
                                        )}

                                        {/* Edit Button Overlay (Visible on Hover via CSS) */}
                                        <div className={styles.editButtonOverlay}>
                                            <Button
                                                as={Link}
                                                to={`/gallery/${img.id}/tags`}
                                                icon
                                                circular
                                                color="blue"
                                                size="small"
                                                onClick={(e: any) => e.stopPropagation()} // Important: stop click from selecting image
                                                style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 12 }}
                                            >
                                                <Icon name="pencil" />
                                            </Button>
                                        </div>

                                        {/* Checkmark Overlay (Visible only when selected) */}
                                        {isSelected && (
                                            <div style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
                                                <Icon name="check circle" size="large" color="blue" />
                                            </div>
                                        )}
                                    </div>
                                </Grid.Column>
                            );
                        })}
                    </Grid>

                    {/* An empty grid needs saying out loud now that switching galleries makes
                        it easy to land in one. Previously this only happened to an org with no
                        images at all. */}
                    {!loading && data?.imagesConnection.items.length === 0 && (
                        <Message
                            info
                            header={currentGallery ? `${currentGallery.name} is empty` : 'Nothing here yet'}
                            content={canUploadHere
                                ? 'Upload something to get started.'
                                : 'No images have been added to this gallery.'}
                        />
                    )}

                    {data?.imagesConnection.hasNextPage && (
                        <div style={{ textAlign: 'center', marginTop: '2em' }}>
                            <Button basic color="blue" loading={loading} onClick={() => fetchMore({ variables: { cursor: data.imagesConnection.nextCursor }, updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult ? { imagesConnection: { ...fetchMoreResult.imagesConnection, items: [...prev.imagesConnection.items, ...fetchMoreResult.imagesConnection.items] } } : prev })} content="Load More" />
                        </div>
                    )}
                </Container>
            </Segment>
        </ResponsiveContainer>
    );
};

export { AllGalleryView };
